import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import type { Href, Page } from "@zirro/schema";
import { LINK_PATTERN, ValidatedInput, required } from "./ValidatedInput";

export interface NavLinkValue {
  label: string;
  href: Href;
}

const EXTERNAL = "__external";
const validLink = (value: string) =>
  LINK_PATTERN.test(value) ? null : "Start with https://, mailto: or tel:";

/** Choose where a link goes: one of the site's pages, or a web/email/phone link. */
export function HrefPicker({
  value,
  pages,
  onChange,
  label = "Goes to",
}: {
  value: Href;
  pages: Page[];
  onChange: (href: Href) => void;
  label?: string;
}) {
  const external = value.type === "url";
  return (
    <div className="zirro-href">
      <label className="zirro-field">
        <span>{label}</span>
        <select
          value={external ? EXTERNAL : value.pageId}
          onChange={(e) =>
            onChange(
              e.target.value === EXTERNAL
                ? { type: "url", url: "https://" }
                : { type: "page", pageId: e.target.value },
            )
          }
        >
          {pages.map((page) => (
            <option key={page.id} value={page.id}>
              {page.title} ({page.slug})
            </option>
          ))}
          <option value={EXTERNAL}>Web, email or phone link…</option>
        </select>
      </label>
      {external ? (
        <label className="zirro-field">
          <span className="sr-only">Link address</span>
          <ValidatedInput
            type="text"
            value={value.url}
            placeholder="https://, mailto: or tel:"
            validate={validLink}
            onCommit={(url) => onChange({ type: "url", url })}
          />
        </label>
      ) : null}
    </div>
  );
}

/** Edit, add, reorder and remove a menu's links. */
export function LinkListEditor({
  title,
  links,
  pages,
  max = 7,
  onChange,
}: {
  title: string;
  links: NavLinkValue[];
  pages: Page[];
  max?: number;
  onChange: (links: NavLinkValue[]) => void;
}) {
  const update = (index: number, patch: Partial<NavLinkValue>) =>
    onChange(links.map((link, i) => (i === index ? { ...link, ...patch } : link)));
  const move = (index: number, by: -1 | 1) => {
    const next = [...links];
    const [item] = next.splice(index, 1);
    next.splice(index + by, 0, item!);
    onChange(next);
  };
  // New links point at the first page the menu doesn't link to yet.
  const linked = new Set(links.flatMap((l) => (l.href.type === "page" ? [l.href.pageId] : [])));
  const nextPage = pages.find((p) => !linked.has(p.id)) ?? pages[0];

  return (
    <div className="zirro-field-group">
      <h5>
        {title}
        <span className="zirro-counter">
          {links.length} / {max}
        </span>
      </h5>
      {links.length === 0 ? <p className="zirro-hint">No links yet.</p> : null}
      {links.map((link, i) => (
        <div key={i} className="zirro-link-row">
          <div className="zirro-link-row__head">
            <ValidatedInput
              type="text"
              value={link.label}
              maxLength={60}
              aria-label={`Link ${i + 1} text`}
              placeholder="Link text"
              validate={required("Link text can't be empty")}
              onCommit={(label) => update(i, { label })}
            />
            <button
              type="button"
              className="zirro-btn-mini"
              disabled={i === 0}
              onClick={() => move(i, -1)}
              aria-label={`Move ${link.label || "link"} up`}
            >
              <ArrowUp size={12} />
            </button>
            <button
              type="button"
              className="zirro-btn-mini"
              disabled={i === links.length - 1}
              onClick={() => move(i, 1)}
              aria-label={`Move ${link.label || "link"} down`}
            >
              <ArrowDown size={12} />
            </button>
            <button
              type="button"
              className="zirro-btn-mini zirro-btn-mini--danger"
              onClick={() => onChange(links.filter((_, j) => j !== i))}
              aria-label={`Remove ${link.label || "link"}`}
            >
              <Trash2 size={12} />
            </button>
          </div>
          <HrefPicker value={link.href} pages={pages} onChange={(href) => update(i, { href })} />
        </div>
      ))}
      {links.length < max && nextPage ? (
        <button
          type="button"
          className="zirro-btn zirro-btn-secondary zirro-btn--block"
          onClick={() =>
            onChange([...links, { label: nextPage.navLabel || nextPage.title, href: { type: "page", pageId: nextPage.id } }])
          }
        >
          <Plus size={13} aria-hidden="true" />
          Add link
        </button>
      ) : null}
    </div>
  );
}
